/**
 * LookupPicker — generic autocomplete + chip multi-select primitive
 * (Block 6.B.2 / ADR-017 cross-cutting #7 + ADR-020 cascade).
 *
 * The load-bearing component. Subcategory / Specialty / Service-Area
 * pickers all compose this.
 *
 * Behavior:
 *   - Read mode: chips for each `selected[]` item; "+ Add {label}" button.
 *   - Click "+ Add" → morphs to autocomplete input.
 *   - Focus (empty q) → loadOptions("") → top-N rows (Q4 show-on-focus).
 *   - Type with 200ms debounce + min-2-chars (Q3) → filter list.
 *   - Click a row → onAdd(slug); chip appears; dropdown stays open.
 *   - "+ Add 'X' as new {typeLabel}" affordance:
 *     - Appears when q.length ≥ 2 and no exact display_name match.
 *     - Data-steward path: enabled; click → seeding.onCreate(q) → onAdd auto.
 *       Slug autopreview shown below display_name (Q2).
 *     - Non-steward path: disabled with subtitle "Ask a data steward to
 *       add" (F-O-1: subtitle says only this in 6.B.2; 6.C extends with
 *       suggested-edits-queue link when that surface lands).
 *
 * ADR-017 rubric:
 *   - F1: no modal (inline dropdown).
 *   - F2: in-place (no separate Manage page).
 *   - F4: × always visible on chips (not hover-only); 44px tap target.
 *   - F5: autocomplete IS the lookup; no free-text fallback.
 *   - F6: one-click add.
 *   - F7: no confirm on remove (routine).
 *   - F8: 375px-first; full-width input.
 *   - F11: inline-confirmation (preview + click) before lookup row created.
 *   - F12: no refusal-to-commit pre-flow (existing values commit fine).
 *   - F13: non-stewards see disabled affordance, not silent skip.
 *
 * Accessibility: combobox + listbox + option ARIA pattern;
 * aria-autocomplete=list; aria-expanded; aria-activedescendant; Down/Up
 * arrows navigate; Enter selects; Esc closes.
 */

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import { SearchField } from '@/components/primitives/SearchField';
import { slugify } from '@/lib/lookups';
import { errorCodeOf, pageReadText, pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

const DEBOUNCE_MS = 200;
const MIN_CHARS = 2;
const ON_FOCUS_LIMIT = 10;

export interface LookupPickerSeeding {
  /** Whether the current user can add new lookup rows (data-steward gate). */
  canAdd: boolean;
  /** Create a new lookup row server-side; returns the new slug + display. */
  onCreate: (displayName: string) => Promise<{ slug: string; display_name: string }>;
  /** Type label for the "+ Add 'X' as new {typeLabel}" affordance. */
  typeLabel: string;
}

export interface LookupPickerProps<T> {
  /** Currently-selected items rendered as chips. */
  selected: T[];
  /**
   * Display label for the dropdown option list. Verbose / disambiguated
   * context — keeps cross-row disambiguation (e.g. "Passaic County, NJ"
   * for service areas; state suffix earns its place in option list).
   *
   * Also the fallback for chip rendering when getChipLabel is undefined.
   */
  getLabel: (item: T) => string;
  /**
   * Block 6.G.1 D5 — optional terser label for the selected-chips row.
   * Falls back to getLabel when undefined. Surface for terse-display
   * contexts (e.g. service-area chips: "Passaic County" vs dropdown's
   * "Passaic County, NJ"). Separate formatter per context per design
   * plan r2 §3.3 — not a flag on a single formatter; two named props.
   */
  getChipLabel?: (item: T) => string;
  /** Stable id for keying + onRemove. */
  getId: (item: T) => string;
  /** Slug accessor for option list (so picker calls onAdd(slug)). */
  getSlug: (item: T) => string;
  /** Optional secondary line for option rendering (e.g. "(county)" suffix). */
  getOptionSubtitle?: (item: T) => string | null;
  /** Fetch matching items from API. Called on focus + on debounced type. */
  loadOptions: (q: string, limit: number) => Promise<T[]>;
  /** POST /providers/{id}/{lookup-type}/{slug}. */
  onAdd: (slug: string) => Promise<void>;
  /** DELETE /providers/{id}/{lookup-type}/{id}. */
  onRemove: (id: string) => Promise<void>;
  /** Lookup-seeding affordance per ADR-020. */
  seeding: LookupPickerSeeding;
  /** ARIA + visible button label (e.g. "subcategory"). */
  label: string;
  /** Optional placeholder for the autocomplete input. */
  placeholder?: string;
  /**
   * Phase 1.7.2 — optional callback fired when the picker's internal
   * `open` state changes (collapsed chips ↔ expanded input/dropdown).
   * Consumers can sync ambient UI (e.g. ServiceAreaPicker uses this to
   * hide its type-filter chips when the picker is closed, eliminating
   * the "ambient pills" leak per Phase 1.7.2 opener). This is a tactical
   * hook; Phase 1.8 picker redesign will likely supersede.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Block 6.G.4 — selected-chip variant per peace-hub canonical
   * (ResourceDetail.jsx:312 + :322 + :338). Three values:
   *   - 'sage'    — peace-hub canonical for service-types /
   *                 service-delivery-model / payment-types / county
   *                 chips: `bg-sage-50 text-sage-600 rounded-lg`
   *   - 'warm'    — peace-hub canonical for specialties pills:
   *                 `bg-warm-100 text-warm-600 rounded-lg`
   *   - 'neutral' — pre-6.G.4 chunky-grey default; preserved for
   *                 back-compat / contexts without a canonical anchor.
   * Default 'neutral' so existing callers that don't opt in keep
   * their current treatment.
   */
  chipVariant?: 'sage' | 'warm' | 'neutral' | 'tag' | 'service' | 'specialty';
  /**
   * Block 6.G.4 — override for the on-focus load limit. Defaults to
   * ON_FOCUS_LIMIT (10) — right-sized for autocomplete dropdowns and
   * for the service-area picker (Phase 1.8 will redesign that picker
   * for progressive disclosure of 600+ ZIPs etc.). Subcategory +
   * specialty pickers override to 200 so the user can browse the full
   * lookup table (75 subcats + 119 specialties total).
   */
  loadOptionsLimit?: number;
  /**
   * Phase 1.11 — read-only render when false (no "+ Add" button; no ×
   * remove on chips; no autocomplete). Defaults to true for backward
   * compat. Consumers gate on `canDirectWrite(whoami)` at the call site
   * (OrganizationDetail.jsx etc.); mso_staff + the three middle roles
   * (post-1.11) see read-only.
   */
  canEdit?: boolean;
}

export function LookupPicker<T>({
  selected,
  getLabel,
  getChipLabel,
  getId,
  getSlug,
  getOptionSubtitle,
  loadOptions,
  onAdd,
  onRemove,
  seeding,
  label,
  placeholder,
  onOpenChange,
  chipVariant = 'neutral',
  loadOptionsLimit,
  canEdit = true,
}: LookupPickerProps<T>): ReactNode {
  // Block 6.G.1 D5 — chip-label resolution. getChipLabel earns its place
  // when chips need a terser shape than dropdown options; falls back to
  // getLabel otherwise so existing consumers (subcategory/specialty
  // pickers) don't change behavior.
  const chipLabel = (item: T): string => (getChipLabel ?? getLabel)(item);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const contact = useSupportContact();
  // A failed save's sentence; a failed load keeps its error and is worded
  // at render, so the load effect does not depend on the contact.
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const comboRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();
  // Used to construct the F-O-1 closure link (Block 6.C.2 amendment) on
  // the disabled "+ Add" affordance for non-stewards. Subtitle now
  // points to /t/:tenantSlug/my-suggestions where the CM can track + add
  // suggestions via the queue surface.
  // §m: keep this route in sync with src/routes.tsx's MySuggestions path.
  const { tenantSlug } = useTenant();

  // Debounce the query.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // Load options when open + debounced query changes. Empty query → top-N.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const q = debouncedQ.trim();
    if (q.length > 0 && q.length < MIN_CHARS) {
      // Below min-chars: clear options (avoid noisy single-char hits).
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMsg(null);
    setLoadError(null);
    loadOptions(q, q.length === 0 ? (loadOptionsLimit ?? ON_FOCUS_LIMIT) : 50)
      .then((rows) => {
        if (!cancelled) {
          setOptions(rows);
          setActiveIndex(0);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, debouncedQ, loadOptions]);

  const selectedSlugs = useMemo(
    () => new Set(selected.map((s) => getSlug(s))),
    [selected, getSlug],
  );

  const filteredOptions = useMemo(
    () => options.filter((o) => !selectedSlugs.has(getSlug(o))),
    [options, selectedSlugs, getSlug],
  );

  const exactMatch = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    return filteredOptions.some((o) => getLabel(o).toLowerCase() === trimmed);
  }, [filteredOptions, query, getLabel]);

  const shownError =
    errorMsg ?? (loadError !== null ? pageReadText(loadError, contact, `the ${label} list`) : null);

  const showAddAffordance = query.trim().length >= MIN_CHARS && !exactMatch && !loading;

  function openPicker(): void {
    setOpen(true);
    onOpenChange?.(true);
    setQuery('');
    setDebouncedQ('');
    // useEffect picks up loadOptions("") with limit 10.
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function closePicker(): void {
    setOpen(false);
    onOpenChange?.(false);
    setQuery('');
    setOptions([]);
    setActiveIndex(0);
    setErrorMsg(null);
    setLoadError(null);
  }

  async function handlePick(item: T): Promise<void> {
    setErrorMsg(null);
    try {
      await onAdd(getSlug(item));
      // Stay open for sequential picks; clear query.
      setQuery('');
      setDebouncedQ('');
    } catch (e) {
      setErrorMsg(pageWriteText(e, contact));
    }
  }

  async function handleRemove(item: T): Promise<void> {
    setErrorMsg(null);
    try {
      await onRemove(getId(item));
    } catch (e) {
      setErrorMsg(pageWriteText(e, contact));
    }
  }

  async function handleCreateNew(): Promise<void> {
    if (!seeding.canAdd) return;
    setCreating(true);
    setErrorMsg(null);
    try {
      const created = await seeding.onCreate(query.trim());
      // Auto-add the newly-created slug.
      await onAdd(created.slug);
      setQuery('');
      setDebouncedQ('');
    } catch (e) {
      setErrorMsg(
        errorCodeOf(e) === 'lookup_slug_conflict'
          ? `That ${label} already exists. Pick it from the list.`
          : pageWriteText(e, contact),
      );
    } finally {
      setCreating(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = filteredOptions[activeIndex];
      if (target) void handlePick(target);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closePicker();
    }
  }

  // Phase 1.8.2 — chip pill convention aligned with TieredAreaPicker
  // (rounded-pill px-2 py-1 text-xs). Selected lookup chips were
  // visually heavy ("big ugly rectangles" per Amanda's operate-and-monitor
  // signal); shrunk to match the service-area chip pill style.
  // Remove button keeps an accessible tap target without dominating the
  // chip's visual mass.
  // Directory detail rebuild §3 — content chips compose the Chip primitive's
  // treatments (service = sage-50/sage-600, specialty = warm-100/warm-700,
  // neutral = sand/ink-soft) at the tight chip radius. The legacy sage/warm/
  // tag names are kept for back-compat but now render the same brand set.
  const chipClass = {
    service: 'inline-flex items-center gap-1 bg-sage-50 text-sage-600 rounded-tight px-2 py-0.5 text-[11px] font-medium',
    specialty: 'inline-flex items-center gap-1 bg-warm-100 text-warm-700 rounded-tight px-2 py-0.5 text-[11px] font-medium',
    neutral: 'inline-flex items-center gap-1 bg-sand text-ink-soft rounded-tight px-2 py-0.5 text-[11px] font-medium',
    sage: 'inline-flex items-center gap-1 bg-sage-50 text-sage-600 rounded-tight px-2 py-0.5 text-[11px] font-medium',
    warm: 'inline-flex items-center gap-1 bg-warm-100 text-warm-700 rounded-tight px-2 py-0.5 text-[11px] font-medium',
    tag: 'inline-flex items-center gap-1 bg-sage-50 text-sage-600 rounded-tight px-2 py-0.5 text-[11px] font-medium',
  }[chipVariant];
  // The removal × rests at reduced opacity, inheriting the chip's own hue.
  const chipRemoveColor = 'opacity-60 hover:opacity-100';

  // Phase 1.11 — read-only render path: chips without × removal, no
  // "+ Add" button, no autocomplete dropdown. Empty-state in read-only
  // returns null so the caller can render its own placeholder if
  // desired (consistent with the prior empty-state-is-the-caller's-job
  // convention on this picker).
  if (!canEdit) {
    if (selected.length === 0) return null;
    return (
      <div>
        <div className="flex flex-wrap gap-2 items-center">
          {selected.map((item) => (
            <span key={getId(item)} className={chipClass}>
              <span>{chipLabel(item)}</span>
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Selected chips */}
      <div className="flex flex-wrap gap-2 items-center">
        {selected.map((item) => (
          <span
            key={getId(item)}
            className={chipClass}
          >
            <span>{chipLabel(item)}</span>
            <button
              type="button"
              aria-label={`Remove ${chipLabel(item)}`}
              onClick={() => void handleRemove(item)}
              className={`ml-0.5 inline-flex items-center cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ${chipRemoveColor}`}
            >
              <X size={12} aria-hidden="true" />
            </button>
          </span>
        ))}
        {!open && (
          <>
            {/* Directory detail rebuild §3 — a 4px dot divider sets the add
                action apart from the content chips (adding is an action,
                never chip-weight content). */}
            {selected.length > 0 && (
              <span aria-hidden="true" className="h-1 w-1 shrink-0 rounded-pill bg-sage-200" />
            )}
            <button
              type="button"
              onClick={openPicker}
              // The dashed-border ghost add pill (§3): dashed sage-300 border,
              // sage-400 text; hover fills sage-400 border + sage-50 wash. The
              // "+ Add {label}" text is kept verbatim (accessible-name pin).
              className="inline-flex items-center gap-1 rounded-tight border border-dashed border-sage-300 px-2 py-0.5 text-[11px] font-medium text-sage-400 hover:border-sage-400 hover:bg-sage-50 hover:text-sage-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              + Add {label}
            </button>
          </>
        )}
      </div>

      {/* Open picker: input + dropdown */}
      {open && (
        <div className="mt-2">
          <div
            ref={comboRef}
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-haspopup="listbox"
          >
            <SearchField
              ref={inputRef}
              value={query}
              onChange={setQuery}
              onKeyDown={handleKeyDown}
              onBlur={(e) => {
                // Focus that stays inside the combobox (Tab to the clear
                // button) keeps the picker open, so the keyboard clear works.
                if (comboRef.current?.contains(e.relatedTarget as Node)) return;
                // Delay to allow clicks on dropdown to register.
                setTimeout(() => closePicker(), 150);
              }}
              placeholder={placeholder ?? `Search ${label}`}
              aria-label={`Search ${label}`}
              aria-autocomplete="list"
              aria-controls={listboxId}
              testId={`lookup-search-${label}`}
            />
          </div>

          <ul
            id={listboxId}
            role="listbox"
            aria-label={label}
            className="mt-1 border border-sage-800/[8%] rounded-md bg-paper shadow-sm max-h-64 overflow-y-auto"
          >
            {loading && (
              <li className="px-3 py-2 text-sm text-ink-soft" aria-live="polite">
                Loading…
              </li>
            )}
            {!loading && filteredOptions.length === 0 && !showAddAffordance && (
              <li className="px-3 py-2 text-sm text-ink-soft">
                {query.trim().length === 0
                  ? 'Type to search…'
                  : query.trim().length < MIN_CHARS
                    ? `Type at least ${MIN_CHARS} characters`
                    : 'No matches'}
              </li>
            )}
            {filteredOptions.map((opt, i) => (
              <li
                key={getId(opt)}
                role="option"
                aria-selected={i === activeIndex}
                onMouseDown={(e) => {
                  // mouseDown to fire before the input's onBlur close.
                  e.preventDefault();
                  void handlePick(opt);
                }}
                className={
                  'px-3 py-2 text-sm cursor-pointer min-h-11 ' +
                  (i === activeIndex ? 'bg-sage-50' : 'hover:bg-sand')
                }
              >
                <div>{getLabel(opt)}</div>
                {getOptionSubtitle && (
                  <div className="text-xs text-ink-soft">
                    {getOptionSubtitle(opt) ?? ''}
                  </div>
                )}
              </li>
            ))}
            {showAddAffordance && (
              <li className="border-t border-sage-800/[8%] px-3 py-2">
                {seeding.canAdd ? (
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      void handleCreateNew();
                    }}
                    disabled={creating}
                    className="w-full text-left min-h-11 text-sm text-sage-800 hover:bg-sand disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                  >
                    {creating
                      ? 'Adding…'
                      : `+ Add "${query.trim()}" as new ${seeding.typeLabel}`}
                    <div className="text-xs text-ink-soft mt-0.5">
                      Slug will be: {slugify(query.trim())}
                    </div>
                  </button>
                ) : (
                  <div className="text-sm text-ink-soft">
                    <button
                      type="button"
                      disabled
                      className="block w-full text-left min-h-11 text-ink-soft cursor-not-allowed"
                    >
                      + Add "{query.trim()}" as new {seeding.typeLabel}
                    </button>
                    <div className="text-xs text-ink-soft mt-0.5">
                      Ask a data steward to add, or{' '}
                      <Link
                        to={`/t/${tenantSlug}/my-suggestions`}
                        className="underline text-sage-800 hover:text-sage-800"
                      >
                        suggest via the queue ↗
                      </Link>
                    </div>
                  </div>
                )}
              </li>
            )}
          </ul>

          {shownError !== null && (
            <div role="alert" className="mt-1 text-xs text-red-700" data-testid="lookup-picker-error">
              ⚠ {shownError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
